import { useState, useEffect, useCallback, useRef, useContext, createContext } from 'react';
// Use the v9 modular SDK for better performance and smaller bundle sizes
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { 
    getFirestore, 
    doc, 
    setDoc, 
    getDoc, 
    collection, 
    onSnapshot, 
    addDoc, 
    updateDoc, 
    arrayUnion,
    arrayRemove,
    query,
    where,
    Timestamp,
    deleteDoc,
    getDocs
} from 'firebase/firestore';
import { CheckCircle, Settings, Users, ClipboardList, CalendarPlus, Bell, X, Menu, Trash2, Edit, ArrowUp, ArrowDown, PlusCircle, Music, Eye, EyeOff, Filter, Plus, Minus, ChevronDown, ChevronRight, Volume2, VolumeX, PlayCircle } from 'lucide-react';

// --- [Logo Component] ---
const VantageLogo = ({ className }) => (
    <svg className={className} viewBox="0 0 700 700" xmlns="http://www.w3.org/2000/svg">
      <rect fill="white" width="700" height="700"/>
      <polygon fill="#FF9E5C" points="89.2,687.52 15.92,687.52 15.92,117.6 "/>
      <polygon fill="#E55C00" points="684.08,85.3 684.08,687.52 418.45,687.52 "/>
      <polygon fill="#A9DDF7" points="15.92,12.48 684.08,12.48 684.08,60.98 471.35,60.98 293.04,481.93 247.89,60.98 15.92,60.98 "/>
    </svg>
);

// --- [Firebase Configuration] ---
const firebaseConfig = {
  apiKey: "AIzaSyClMn8t_kvyaNfcyDqeKnawGU6SQlmlJzQ",
  authDomain: "vantage-2cc17.firebaseapp.com",
  projectId: "vantage-2cc17",
  storageBucket: "vantage-2cc17.appspot.com",
  messagingSenderId: "322251178475",
  appId: "1:322251178475:web:3793385d13ad44911a19ae",
  measurementId: "G-6TPNF0MBE4"
};

// --- [Firebase Initialization] ---
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'vantage-default';

// --- [Helper Functions] ---
const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    let videoId = null;
    // Standard youtube.com/watch?v=... links
    const urlParts = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/);
    if (urlParts && urlParts[2].length === 11) {
        videoId = urlParts[2];
    }
    
    if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    return null;
};

// --- [Audio Context for Sound Notifications] ---
const AudioContext = createContext();

const AudioProvider = ({ children }) => {
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const synth = useRef(null);

    useEffect(() => {
        if (isUnlocked && !synth.current) {
            // Initialize synth only after user interaction
            synth.current = new window.Tone.Synth().toDestination();
        }
    }, [isUnlocked]);

    const unlockAudio = async () => {
        if (!isUnlocked) {
            await window.Tone.start();
            setIsUnlocked(true);
            console.log("Audio context started.");
        }
        setIsMuted(current => !current);
    };

    const playSound = (soundType) => {
        if (!isUnlocked || isMuted || !synth.current) return;
        
        try {
            switch (soundType) {
                case 'completed':
                    synth.current.triggerAttackRelease("C5", "8n");
                    break;
                case 'yellow':
                    synth.current.triggerAttackRelease("G4", "16n");
                    break;
                case 'red':
                    synth.current.triggerAttackRelease("A4", "8n", window.Tone.now());
                    synth.current.triggerAttackRelease("A4", "8n", window.Tone.now() + 0.2);
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error("Failed to play sound:", error);
        }
    };

    return (
        <AudioContext.Provider value={{ unlockAudio, playSound, isMuted, isUnlocked }}>
            {children}
        </AudioContext.Provider>
    );
};

// --- [Custom Hook to track previous state] ---
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}


// --- [Main App Component] ---
export default function App() {
    const [page, setPage] = useState('volunteer');
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
            } else {
                try {
                    await signInAnonymously(auth);
                } catch (error) {
                    console.error("Anonymous sign-in failed:", error);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const navigateTo = useCallback((targetPage) => {
        setPage(targetPage);
    }, []);

    if (loading) {
        return <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center">Loading VANTAGE...</div>;
    }

    return (
        <AudioProvider>
            <div className="bg-gray-900 text-white min-h-screen font-sans">
                {page === 'volunteer' && <VolunteerDashboard navigateTo={navigateTo} user={user} />}
                {page === 'leaderLogin' && <LeaderLogin navigateTo={navigateTo} />}
                {page === 'leaderConsole' && <LeaderConsole navigateTo={navigateTo} user={user} />}
            </div>
        </AudioProvider>
    );
}

// --- [Video Player Component] ---
const VideoPlayerModal = ({ url, onClose }) => {
    if (!url) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-gray-900 p-4 rounded-lg w-full max-w-4xl relative aspect-video" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute -top-4 -right-4 text-white bg-gray-800 rounded-full p-1 z-10"><X /></button>
                <iframe
                    className="w-full h-full rounded"
                    src={url}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                ></iframe>
            </div>
        </div>
    );
};


// --- [Volunteer Dashboard Component] ---
const VolunteerDashboard = ({ navigateTo, user }) => {
    const [currentEvent, setCurrentEvent] = useState(null);
    const [isEventSelectorOpen, setIsEventSelectorOpen] = useState(true);
    const [isCheckInOpen, setIsCheckInOpen] = useState(false);
    const [isChecklistOpen, setIsChecklistOpen] = useState(false);
    const [isUserSwitcherOpen, setIsUserSwitcherOpen] = useState(false);
    const [isEventEditorOpen, setIsEventEditorOpen] = useState(false);
    const [checkedInUser, setCheckedInUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [eventSongs, setEventSongs] = useState([]);
    const [milestones, setMilestones] = useState([]);
    const [completedMilestones, setCompletedMilestones] = useState([]);
    const [showCompletedMilestones, setShowCompletedMilestones] = useState(true);
    const [checklist, setChecklist] = useState([]);
    const [playingVideoUrl, setPlayingVideoUrl] = useState(null);
    const [, setNow] = useState(new Date());

    const { playSound, unlockAudio, isMuted, isUnlocked } = useContext(AudioContext);
    const prevMilestones = usePrevious(milestones);
    const prevCompletedMilestones = usePrevious(completedMilestones);

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(timer);
    }, []);
    
    useEffect(() => {
        if (!user) return;
        const unsubRoles = onSnapshot(collection(db, `artifacts/${appId}/public/data/roles`), (snapshot) => {
            setRoles(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubUsers = onSnapshot(collection(db, `artifacts/${appId}/public/data/users`), (snapshot) => {
            setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => { unsubRoles(); unsubUsers(); };
    }, [user]);

    useEffect(() => {
        if (!currentEvent || !user) return;
        const eventRef = doc(db, `artifacts/${appId}/public/data/events`, currentEvent.id);
        const unsubEvent = onSnapshot(eventRef, (docSnap) => {
            if (docSnap.exists()) {
                const eventData = docSnap.data();
                setCurrentEvent({ id: docSnap.id, ...eventData });
                setEventSongs(eventData.songs || []);
                const sortedMilestones = (eventData.milestones || []).sort((a, b) => (a.time || "23:59").localeCompare(b.time || "23:59"));
                setMilestones(sortedMilestones);
                setCompletedMilestones(eventData.milestoneCompletions || []);
            }
        });
        return () => unsubEvent();
    }, [currentEvent?.id, user]);
    
    useEffect(() => {
        if (checkedInUser && roles.length > 0) {
            const role = roles.find(r => r.id === checkedInUser.roleId);
            setChecklist(role?.checklist || []);
        } else {
            setChecklist([]);
        }
    }, [checkedInUser, roles]);

    const getMilestoneStatus = useCallback((milestone, completions) => {
        const isCompleted = completions.some(c => c.milestoneName === milestone.name);
        if (isCompleted) return { bg: 'bg-green-500', text: 'text-white', status: 'completed' };
        if (!milestone?.time) return { bg: 'bg-gray-700', text: 'text-white', status: 'pending' };
        const now = new Date();
        const milestoneTime = new Date(`${new Date().toDateString()} ${milestone.time}`);
        const diffMinutes = (milestoneTime - now) / 1000 / 60;
        const redThreshold = milestone.redThreshold || 0;
        const yellowThreshold = milestone.yellowThreshold || 30;
        if (diffMinutes < redThreshold) return { bg: 'bg-red-500', text: 'text-white', status: 'red' };
        if (diffMinutes <= yellowThreshold) return { bg: 'bg-yellow-500', text: 'text-black', status: 'yellow' };
        return { bg: 'bg-gray-700', text: 'text-white', status: 'pending' };
    }, []);

     // Effect to check for milestone status changes and play sounds
    useEffect(() => {
        if (!prevMilestones || !prevCompletedMilestones) return;

        milestones.forEach(milestone => {
            const currentStatus = getMilestoneStatus(milestone, completedMilestones).status;
            
            const prevMilestoneData = prevMilestones.find(m => m.name === milestone.name);
            if (!prevMilestoneData) return;

            const wasCompleted = prevCompletedMilestones.some(c => c.milestoneName === milestone.name);
            const isCompleted = completedMilestones.some(c => c.milestoneName === milestone.name);

            // Play sound when a milestone is newly completed
            if (isCompleted && !wasCompleted) {
                playSound('completed');
                return; // Don't play other sounds if it was just completed
            }
            
            // For non-completed milestones, check for color status changes
            if (!isCompleted) {
                 const prevStatus = getMilestoneStatus(prevMilestoneData, prevCompletedMilestones).status;
                if (currentStatus !== prevStatus) {
                    if(currentStatus === 'yellow') playSound('yellow');
                    if(currentStatus === 'red') playSound('red');
                }
            }
        });

    }, [milestones, completedMilestones, prevMilestones, prevCompletedMilestones, getMilestoneStatus, playSound]);

    const handleEventSelected = (event) => {
        setCurrentEvent(event);
        setIsEventSelectorOpen(false);
    };

    const handleCheckIn = (roleId, userId, name) => {
        setCheckedInUser({ roleId, userId, name });
        setIsCheckInOpen(false);
        if (currentEvent) {
            const eventRef = doc(db, `artifacts/${appId}/public/data/events`, currentEvent.id);
            const checkInRecord = { roleId, userId, name, timestamp: Timestamp.now() };
            const existingCheckIn = currentEvent.checkIns?.find(ci => ci.userId === userId);
            if (!existingCheckIn) {
                updateDoc(eventRef, { checkIns: arrayUnion(checkInRecord) });
            }
        }
    };
    
    const handleChecklistItem = async (itemIndex) => {
        if (!currentEvent || !checkedInUser) return;
        const item = checklist[itemIndex];
        const completionRecord = { completedBy: checkedInUser.name, userId: checkedInUser.userId, timestamp: Timestamp.now() };
        const eventRef = doc(db, `artifacts/${appId}/public/data/events`, currentEvent.id);
        const eventDoc = await getDoc(eventRef);
        let auditLog = eventDoc.data()?.auditLog || [];
        const existingLogIndex = auditLog.findIndex(log => log.task === item.task && log.roleId === checkedInUser.roleId);
        if (existingLogIndex > -1) {
            const isCompleted = auditLog[existingLogIndex].completions.some(c => c.userId === checkedInUser.userId);
            if (!isCompleted) {
                 auditLog[existingLogIndex].completions.push(completionRecord);
            }
        } else {
            auditLog.push({ task: item.task, roleId: checkedInUser.roleId, completions: [completionRecord] });
        }
        await updateDoc(eventRef, { auditLog });
    };

    const handleMilestoneComplete = async (milestoneName) => {
        if (!currentEvent || !checkedInUser) {
            alert("You must be checked in to complete a milestone.");
            return;
        }
        const completionRecord = { milestoneName, completedBy: checkedInUser.name, userId: checkedInUser.userId, timestamp: Timestamp.now() };
        const eventRef = doc(db, `artifacts/${appId}/public/data/events`, currentEvent.id);
        await updateDoc(eventRef, { milestoneCompletions: arrayUnion(completionRecord) });
    };

    const isMilestoneCompleted = (milestone, completions) => {
        return completions.some(c => c.milestoneName === milestone.name);
    }

    return (
        <div className="p-4 md:p-8 relative min-h-screen">
            <header className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-4">
                    <VantageLogo className="w-10 h-10" />
                    <h1 className="text-3xl font-bold tracking-wider">VANTAGE</h1>
                </div>
                <div className="flex items-center space-x-4">
                    {checkedInUser && (
                        <div className="text-right">
                            <p className="text-lg">{checkedInUser.name}</p>
                            <p className="text-sm text-gray-400">{roles.find(r => r.id === checkedInUser.roleId)?.name}</p>
                        </div>
                    )}
                    {currentEvent && (
                        <button onClick={() => setIsUserSwitcherOpen(true)} className="bg-gray-700 p-2 rounded-lg hover:bg-gray-600 transition" title="Switch User">
                            <Users className="w-6 h-6" />
                        </button>
                    )}
                     <button onClick={unlockAudio} className="bg-gray-700 p-2 rounded-lg hover:bg-gray-600 transition" title={isMuted ? "Unmute" : "Mute"}>
                        {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                        {!isUnlocked && <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-gray-900"></div>}
                    </button>
                    <button onClick={() => navigateTo('leaderLogin')} className="bg-gray-700 p-2 rounded-lg hover:bg-gray-600 transition" title="Leader Console">
                        <Settings className="w-6 h-6" />
                    </button>
                </div>
            </header>

            {currentEvent ? (
                <>
                    <div className="text-center mb-8">
                        <h2 className="text-4xl font-light">{currentEvent.name}</h2>
                        {currentEvent.date?.seconds && (
                            <p className="text-gray-400">{new Date(currentEvent.date.seconds * 1000).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-gray-800 p-6 rounded-lg">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-2xl font-semibold">Set List</h3>
                                <button onClick={() => setIsEventEditorOpen(true)} className="p-2 hover:bg-gray-600 rounded-full" title="Edit Set List"><Edit size={18}/></button>
                            </div>
                            <div className="space-y-4">
                                {eventSongs.length > 0 ? eventSongs.map((song, index) => (
                                    <div key={index} className="bg-gray-700 p-3 rounded">
                                        <p className="text-lg font-semibold">{index + 1}. {song.name}</p>
                                        <div className="text-sm text-gray-400 flex justify-between">
                                            <span>{song.soloists || 'N/A'}</span>
                                            <span>Key: {song.key || 'N/A'} | Tempo: {song.tempo || 'N/A'}</span>
                                        </div>
                                    </div>
                                )) : <p className="text-gray-500">No songs have been added.</p>}
                            </div>
                        </div>
                        <div className="bg-gray-800 p-6 rounded-lg">
                             <div className="flex justify-between items-center mb-4">
                                <h3 className="text-2xl font-semibold">Major Milestones</h3>
                                <button onClick={() => setShowCompletedMilestones(!showCompletedMilestones)} className="p-2 hover:bg-gray-600 rounded-full" title={showCompletedMilestones ? "Hide Completed" : "Show Completed"}>
                                    {showCompletedMilestones ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            <div className="space-y-3">
                                {milestones.filter(m => showCompletedMilestones || !isMilestoneCompleted(m, completedMilestones)).map((milestone, index) => {
                                    const isCompleted = isMilestoneCompleted(milestone, completedMilestones);
                                    const status = getMilestoneStatus(milestone, completedMilestones);
                                    const embedUrl = getYoutubeEmbedUrl(milestone.howtoLink);
                                    return (
                                        <div key={index} className={`flex items-center justify-between p-3 rounded transition-colors ${status.bg} ${status.text}`}>
                                            <div className="flex items-center space-x-4">
                                                <button onClick={() => !isCompleted && handleMilestoneComplete(milestone.name)} disabled={isCompleted} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 disabled:cursor-not-allowed ${isCompleted ? 'border-transparent' : 'border-current'}`}>
                                                    {isCompleted && <CheckCircle className="w-5 h-5" />}
                                                </button>
                                                <div className="flex items-center space-x-2">
                                                    <span className={`text-lg ${isCompleted ? 'line-through opacity-70' : ''}`}>{milestone.name}</span>
                                                    {embedUrl && (
                                                        <button onClick={() => setPlayingVideoUrl(embedUrl)} className="opacity-80 hover:opacity-100 transition-opacity">
                                                            <PlayCircle size={20} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-lg font-mono">{milestone.time}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="fixed bottom-8 right-8 flex flex-col space-y-4">
                        {!checkedInUser && (
                             <button onClick={() => setIsCheckInOpen(true)} className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-blue-500 transition text-lg">Check In</button>
                        )}
                        {checkedInUser && (
                            <button onClick={() => setIsChecklistOpen(true)} className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-green-500 transition text-lg flex items-center space-x-2">
                                <ClipboardList /><span>My Checklist</span>
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <div className="flex flex-col items-center justify-center h-[60vh]">
                    <p className="text-2xl text-gray-500 mb-4">No event selected.</p>
                    <button onClick={() => setIsEventSelectorOpen(true)} className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-blue-500 transition text-lg">Select Event</button>
                </div>
            )}
            {isEventSelectorOpen && <EventSelector onSelect={handleEventSelected} onDismiss={() => setIsEventSelectorOpen(false)} />}
            {isCheckInOpen && <CheckInModal roles={roles} users={users} onCheckIn={handleCheckIn} onClose={() => setIsCheckInOpen(false)} />}
            {isChecklistOpen && <ChecklistDrawer checklist={checklist} onCheckItem={handleChecklistItem} onClose={() => setIsChecklistOpen(false)} currentEvent={currentEvent} checkedInUser={checkedInUser} />}
            {isUserSwitcherOpen && <UserSwitcherModal event={currentEvent} onSelect={setCheckedInUser} onClose={() => setIsUserSwitcherOpen(false)} />}
            {isEventEditorOpen && <EventEditorModal event={currentEvent} onClose={() => setIsEventEditorOpen(false)} />}
            <VideoPlayerModal url={playingVideoUrl} onClose={() => setPlayingVideoUrl(null)} />
        </div>
    );
};

const EventSelector = ({ onSelect, onDismiss }) => {
    const [events, setEvents] = useState([]);
    const [eventOptions, setEventOptions] = useState([]);
    // Default date to today
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
        return today.toISOString().split('T')[0];
    });
    const [selectedOption, setSelectedOption] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false); // Track if the initial fetch is done

    // Fetch available event types from settings
    useEffect(() => {
        const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'leaderSettings');
        const unsubOptions = onSnapshot(settingsRef, (doc) => {
            if (doc.exists()) {
                setEventOptions(doc.data()?.eventOptions || []);
            }
        });
        return () => unsubOptions();
    }, []);

    // Fetch events for the selected date
    useEffect(() => {
        if (!selectedDate) return;

        setHasLoaded(false); // Reset loading state when the date changes
        setIsCreating(false); // Reset create mode when date changes

        const startOfDay = new Date(selectedDate);
        startOfDay.setUTCHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setUTCHours(23, 59, 59, 999);

        const q = query(
            collection(db, `artifacts/${appId}/public/data/events`),
            where('date', '>=', Timestamp.fromDate(startOfDay)),
            where('date', '<=', Timestamp.fromDate(endOfDay))
        );

        const unsubEvents = onSnapshot(q, (snapshot) => {
            setEvents(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setHasLoaded(true); // Mark that the data has been loaded
        });

        return () => unsubEvents();
    }, [selectedDate]);

    // Create a new event
    const handleCreate = async () => {
        if (!selectedOption || !selectedDate) return;

        const milestonesQuery = query(
            collection(db, `artifacts/${appId}/public/data/milestones`),
            where('assignedEventTypes', 'array-contains', selectedOption)
        );
        const milestonesSnapshot = await getDocs(milestonesQuery);
        const eventMilestones = milestonesSnapshot.docs.map(d => d.data());
        
        const localDate = new Date(selectedDate + 'T12:00:00'); // Default to noon

        const newEvent = {
            name: selectedOption,
            date: Timestamp.fromDate(localDate),
            songs: [],
            milestones: eventMilestones,
            milestoneCompletions: [],
            checkIns: [],
            auditLog: []
        };
        const docRef = await addDoc(collection(db, `artifacts/${appId}/public/data/events`), newEvent);
        onSelect({ id: docRef.id, ...newEvent });
    };
    
    const renderBody = () => {
        if (!hasLoaded) {
            return <p className="text-gray-400 text-center py-8">Loading events...</p>;
        }

        if (events.length > 0 && !isCreating) {
            return (
                <>
                   <p className="text-gray-400 text-center">Today's Events:</p>
                   {events.map(event => (
                       <button key={event.id} onClick={() => onSelect(event)} className="w-full text-left p-4 bg-blue-600 rounded hover:bg-blue-500 transition">
                           <p className="font-semibold text-lg">{event.name}</p>
                           {event.date?.seconds && (
                             <p className="text-sm text-blue-200">Start Time: {new Date(event.date.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                           )}
                       </button>
                   ))}
                   <button onClick={() => setIsCreating(true)} className="w-full text-center p-2 text-blue-400 hover:text-blue-300">
                       Or create a new one...
                   </button>
                </>
            );
        }

        // This block now handles both "no events found" and "user chose to create"
        return (
             <>
                <p className="text-gray-400 text-center">{events.length > 0 ? 'Create a new event:' : 'No events found for this day. Create one below.'}</p>
                <select
                     value={selectedOption}
                     onChange={(e) => setSelectedOption(e.target.value)}
                     className="w-full p-3 bg-gray-700 rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">Select Event Type</option>
                    {eventOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                </select>
                <button onClick={handleCreate} disabled={!selectedOption} className="w-full p-3 bg-green-600 rounded hover:bg-green-500 disabled:bg-gray-500 transition font-bold">
                    Create & Start Event
                </button>
                {/* Show a back button only if there were events to go back to */}
                {events.length > 0 && isCreating && (
                     <button onClick={() => setIsCreating(false)} className="w-full text-center p-2 text-blue-400 hover:text-blue-300">
                        Back to list
                     </button>
                )}
             </>
        );
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg w-full max-w-md relative">
                <button onClick={onDismiss} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X /></button>
                <div className="flex flex-col items-center">
                    <VantageLogo className="w-24 h-24 mb-4" />
                    <h2 className="text-2xl font-bold mb-6">Select or Create Event</h2>
                </div>
                <div className="space-y-4">
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-full p-3 bg-gray-700 rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {renderBody()}
                </div>
            </div>
        </div>
    );
};

const CheckInModal = ({ roles, users, onCheckIn, onClose }) => {
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedUser, setSelectedUser] = useState('');

    const handleConfirm = () => {
        const userObj = users.find(u => u.id === selectedUser);
        if (userObj) {
            onCheckIn(selectedRole, selectedUser, userObj.name);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg w-full max-w-md relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X /></button>
                <h2 className="text-2xl font-bold mb-6">Volunteer Check-in</h2>
                <div className="space-y-4">
                    <select onChange={(e) => setSelectedRole(e.target.value)} value={selectedRole} className="w-full p-3 bg-gray-700 rounded">
                        <option value="">Select Your Role</option>
                        {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                    </select>
                    <select onChange={(e) => setSelectedUser(e.target.value)} value={selectedUser} className="w-full p-3 bg-gray-700 rounded">
                        <option value="">Select Your Name</option>
                        {users.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                    </select>
                    <button onClick={handleConfirm} disabled={!selectedRole || !selectedUser} className="w-full p-3 bg-blue-600 rounded hover:bg-blue-500 disabled:bg-gray-500 transition font-bold">
                        Confirm Check-in
                    </button>
                </div>
            </div>
        </div>
    );
};

const ChecklistDrawer = ({ checklist, onCheckItem, onClose, currentEvent, checkedInUser }) => {
    const [log, setLog] = useState([]);

    // Listen for changes to the event's audit log
    useEffect(() => {
        if(!currentEvent) return;
        const unsub = onSnapshot(doc(db, `artifacts/${appId}/public/data/events`, currentEvent.id), (doc) => {
            if (doc.exists()) {
                setLog(doc.data().auditLog || []);
            }
        });
        return () => unsub();
    }, [currentEvent]);

    // Check if the current user has completed a specific task
    const isItemCompletedByUser = (task) => {
        const taskLog = log.find(l => l.task === task && l.roleId === checkedInUser.roleId);
        return taskLog?.completions.some(c => c.userId === checkedInUser.userId) || false;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose}>
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-gray-800 shadow-2xl p-6 transform transition-transform translate-x-0" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">My Checklist</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
                </div>
                <div className="space-y-3 overflow-y-auto h-[calc(100vh-100px)]">
                    {checklist.map((item, index) => {
                        const completed = isItemCompletedByUser(item.task);
                        return (
                            <div key={index} 
                                 onClick={() => !completed && onCheckItem(index)}
                                 className={`flex items-center p-4 rounded-lg transition cursor-pointer ${completed ? 'bg-green-800 text-gray-400' : 'bg-gray-700 hover:bg-gray-600'}`}>
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-4 ${completed ? 'border-green-500 bg-green-500' : 'border-gray-400'}`}>
                                    {completed && <CheckCircle className="w-5 h-5 text-white" />}
                                </div>
                                <span className={`flex-grow ${completed ? 'line-through' : ''}`}>{item.task}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- [Leader Console Components] ---

const LeaderLogin = ({ navigateTo }) => {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');

    const handleInput = (num) => {
        if (pin.length < 4) {
            setPin(pin + num);
        }
    };

    const handleDelete = () => {
        setPin(pin.slice(0, -1));
    };

    // Effect to verify PIN automatically when 4 digits are entered
    useEffect(() => {
        const verifyPin = async () => {
            if (pin.length !== 4) return;
            setError('');
            try {
                const settingsDoc = await getDoc(doc(db, `artifacts/${appId}/public/data/settings`, 'leaderSettings'));
                if (settingsDoc.exists() && settingsDoc.data()?.pin === pin) {
                    navigateTo('leaderConsole');
                } else {
                    setError('Invalid PIN');
                    setTimeout(() => setPin(''), 1000); // Clear PIN after a short delay
                }
            } catch (e) {
                console.error("PIN verification error:", e);
                setError('Error verifying PIN. A leader must set one first.');
                setTimeout(() => setPin(''), 1000);
            }
        };

        verifyPin();
    }, [pin, navigateTo]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center">
            <h1 className="text-3xl font-bold mb-2">Leader Access</h1>
            <p className="text-gray-400 mb-8">Enter the 4-digit PIN</p>
            <div className="flex space-x-4 mb-4">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className={`w-12 h-12 bg-gray-800 border-2 rounded-lg flex items-center justify-center text-2xl transition-all ${error ? 'border-red-500 animate-shake' : 'border-gray-600'}`}>
                        {pin[i] ? '•' : ''}
                    </div>
                ))}
            </div>
            {error && <p className="text-red-500 mb-4 h-6">{error}</p>}
            <div className="grid grid-cols-3 gap-4 w-full max-w-xs">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                    <button key={num} onClick={() => handleInput(num)} className="p-4 bg-gray-700 rounded-lg text-2xl hover:bg-gray-600 transition">
                        {num}
                    </button>
                ))}
                 <button onClick={() => navigateTo('volunteer')} className="p-4 bg-gray-700 rounded-lg text-lg hover:bg-gray-600 transition">
                    Back
                </button>
                <button onClick={() => handleInput(0)} className="p-4 bg-gray-700 rounded-lg text-2xl hover:bg-gray-600 transition">
                    0
                </button>
                <button onClick={handleDelete} className="p-4 bg-gray-700 rounded-lg text-lg hover:bg-gray-600 transition">
                    DEL
                </button>
            </div>
        </div>
    );
};

const LeaderConsole = ({ navigateTo, user }) => {
    const [activeTab, setActiveTab] = useState('events');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Define the tabs for the leader console
    const tabs = {
        events: { label: 'Events', icon: CalendarPlus, component: <EventsTab user={user} /> },
        songs: { label: 'Songs', icon: Music, component: <SongsTab user={user} /> },
        roles: { label: 'Roles & Checklists', icon: ClipboardList, component: <RolesTab user={user} /> },
        users: { label: 'Users', icon: Users, component: <UsersTab user={user} /> },
        milestones: { label: 'Milestones', icon: Bell, component: <MilestonesTab user={user} /> },
        audit: { label: 'Audit Log', icon: CheckCircle, component: <AuditLogTab user={user} /> },
        settings: { label: 'Settings', icon: Settings, component: <SettingsTab user={user} /> },
    };

    return (
        <div className="flex h-screen">
            {/* Desktop Sidebar */}
            <aside className="hidden md:flex flex-col w-64 bg-gray-800 p-4">
                <div className="flex items-center space-x-2 mb-8">
                    <VantageLogo className="w-8 h-8" />
                    <h1 className="text-xl font-bold">VANTAGE Console</h1>
                </div>
                <nav className="flex flex-col space-y-2">
                    {Object.entries(tabs).map(([key, { label, icon: Icon }]) => (
                        <button key={key} onClick={() => setActiveTab(key)} className={`flex items-center space-x-3 p-3 rounded-lg transition ${activeTab === key ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'}`}>
                            <Icon className="w-5 h-5" />
                            <span>{label}</span>
                        </button>
                    ))}
                </nav>
                 <button onClick={() => navigateTo('volunteer')} className="mt-auto flex items-center space-x-3 p-3 rounded-lg transition hover:bg-gray-700">
                     <span>Back to Volunteer View</span>
                </button>
            </aside>
            
            {/* Mobile Header */}
            <header className="md:hidden bg-gray-800 p-4 w-full flex justify-between items-center fixed top-0 left-0 right-0 z-10">
                 <h1 className="text-xl font-bold">VANTAGE Console</h1>
                 <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}><Menu /></button>
            </header>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                 <div className="md:hidden fixed inset-0 bg-gray-900 z-50 p-4">
                     <div className="flex justify-end mb-4">
                        <button onClick={() => setIsMobileMenuOpen(false)}><X /></button>
                     </div>
                     <nav className="flex flex-col space-y-2">
                        {Object.entries(tabs).map(([key, { label, icon: Icon }]) => (
                            <button key={key} onClick={() => { setActiveTab(key); setIsMobileMenuOpen(false); }} className={`flex items-center space-x-3 p-3 rounded-lg transition ${activeTab === key ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'}`}>
                                <Icon className="w-5 h-5" />
                                <span>{label}</span>
                            </button>
                        ))}
                        <button onClick={() => navigateTo('volunteer')} className="mt-8 flex items-center space-x-3 p-3 rounded-lg transition hover:bg-gray-700">
                           <span>Back to Volunteer View</span>
                        </button>
                    </nav>
                 </div>
            )}

            {/* Main Content Area */}
            <main className="flex-1 p-4 md:p-8 overflow-y-auto mt-16 md:mt-0">
                {tabs[activeTab].component}
            </main>
        </div>
    );
};

// --- [Leader Console Tab Components] ---

const EventsTab = ({ user }) => {
    const [events, setEvents] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    
    // Fetch all events
    useEffect(() => {
        if (!user) return;
        const q = query(collection(db, `artifacts/${appId}/public/data/events`));
        const unsub = onSnapshot(q, (snapshot) => {
            const sortedEvents = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0)); // Sort by date descending
            setEvents(sortedEvents);
        });
        return () => unsub();
    }, [user]);

    // Delete an event after confirmation
    const handleDeleteEvent = async (eventId) => {
        if (window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/events`, eventId));
        }
    };

    const handleOpenModal = (event = null) => {
        setEditingEvent(event);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingEvent(null);
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Manage Events</h2>
                <button onClick={() => handleOpenModal()} className="flex items-center space-x-2 bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-500 transition">
                    <PlusCircle size={20} />
                    <span>Add Event</span>
                </button>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg">
                <div className="space-y-2">
                    {events.map(event => (
                        <div key={event.id} className="flex justify-between items-center p-3 bg-gray-700 rounded">
                            <div>
                                <p className="font-bold">{event.name}</p>
                                <p className="text-sm text-gray-400">
                                    {event.date?.seconds ? new Date(event.date.seconds * 1000).toLocaleDateString() : 'No date'}
                                </p>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => handleOpenModal(event)} className="p-2 hover:bg-gray-600 rounded-full"><Edit size={18}/></button>
                                <button onClick={() => handleDeleteEvent(event.id)} className="p-2 text-red-500 hover:bg-gray-600 rounded-full"><Trash2 size={18}/></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {isModalOpen && <EventModal event={editingEvent} onClose={handleCloseModal} />}
        </div>
    );
};

const EventModal = ({ event, onClose }) => {
    const [eventName, setEventName] = useState(event?.name || '');
    const [eventDate, setEventDate] = useState(event?.date?.seconds ? new Date(event.date.seconds * 1000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    const [eventTime, setEventTime] = useState(event?.date?.seconds ? new Date(event.date.seconds * 1000).toTimeString().substring(0,5) : '12:00');
    const [eventOptions, setEventOptions] = useState([]);
    
    // Song management state
    const [songs, setSongs] = useState([]); // All songs from the database
    const [eventSongs, setEventSongs] = useState(event?.songs || []);
    const [selectedSongId, setSelectedSongId] = useState('');

    // Fetch data for the modal
    useEffect(() => {
        const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'leaderSettings');
        const unsubOptions = onSnapshot(settingsRef, (doc) => {
            if (doc.exists()) setEventOptions(doc.data()?.eventOptions || []);
        });

        const unsubSongs = onSnapshot(collection(db, `artifacts/${appId}/public/data/songs`), (snapshot) => {
            setSongs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubOptions(); unsubSongs(); };
    }, []);

    // Add a selected song to the event's setlist
    const handleAddSong = () => {
        if (!selectedSongId) return;
        const songToAdd = songs.find(s => s.id === selectedSongId);
        if (songToAdd) {
            setEventSongs(prev => [...prev, songToAdd]);
        }
        setSelectedSongId(''); // Reset dropdown
    };

    // Remove a song from the event's setlist
    const handleRemoveSong = (indexToRemove) => {
        setEventSongs(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    // Reorder songs in the event's setlist
    const handleMoveSong = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= eventSongs.length) return;
        const newList = [...eventSongs];
        [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]]; // Swap
        setEventSongs(newList);
    };

    // Save the event data
    const handleSave = async () => {
        if (!eventName || !eventDate || !eventTime) return;
        const dateTime = new Date(`${eventDate}T${eventTime}`);
        
        let eventMilestones = event?.milestones || [];

        if (!event) { // Only fetch new milestones if it's a new event
            const milestonesQuery = query(
                collection(db, `artifacts/${appId}/public/data/milestones`),
                where('assignedEventTypes', 'array-contains', eventName)
            );
            const milestonesSnapshot = await getDocs(milestonesQuery);
            eventMilestones = milestonesSnapshot.docs.map(d => d.data());
        }
        
        const eventData = {
            name: eventName,
            date: Timestamp.fromDate(dateTime),
            songs: eventSongs,
            milestones: eventMilestones,
            // Preserve other fields if editing, or initialize for new event
            milestoneCompletions: event?.milestoneCompletions || [],
            checkIns: event?.checkIns || [],
            auditLog: event?.auditLog || [],
        };

        if (event) {
            await updateDoc(doc(db, `artifacts/${appId}/public/data/events`, event.id), eventData);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/public/data/events`), eventData);
        }
        onClose();
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X /></button>
                <h2 className="text-2xl font-bold mb-6">{event ? 'Edit Event' : 'Add New Event'}</h2>
                <div className="space-y-4">
                    <div>
                        <label className="text-sm text-gray-400">Event Name</label>
                        <select value={eventName} onChange={e => setEventName(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded">
                            <option value="">Select Event Type</option>
                            {eventOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    </div>
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <label className="text-sm text-gray-400">Date</label>
                           <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                        </div>
                        <div>
                           <label className="text-sm text-gray-400">Time</label>
                           <input type="time" value={eventTime} onChange={e => setEventTime(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                        </div>
                    </div>
                    {/* Song Management Section */}
                    <div>
                        <h3 className="text-xl font-semibold mt-6 mb-4">Set List Management</h3>
                        <div className="flex space-x-2 mb-4">
                            <select value={selectedSongId} onChange={e => setSelectedSongId(e.target.value)} className="flex-grow p-2 bg-gray-700 rounded">
                                <option value="">Select a song to add...</option>
                                {songs.map(song => <option key={song.id} value={song.id}>{song.name}</option>)}
                            </select>
                            <button onClick={handleAddSong} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-500">Add</button>
                        </div>
                        <div className="space-y-2">
                            {eventSongs.map((song, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                                    <span>{index + 1}. {song.name}</span>
                                    <div className="flex items-center">
                                        <button onClick={() => handleMoveSong(index, 'up')} className="p-1 hover:bg-gray-600 rounded-full disabled:opacity-50" disabled={index === 0}><ArrowUp size={16}/></button>
                                        <button onClick={() => handleMoveSong(index, 'down')} className="p-1 hover:bg-gray-600 rounded-full disabled:opacity-50" disabled={index === eventSongs.length - 1}><ArrowDown size={16}/></button>
                                        <button onClick={() => handleRemoveSong(index)} className="p-1 text-red-500 hover:bg-gray-600 rounded-full"><Trash2 size={16}/></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="mt-8 flex justify-end">
                    <button onClick={handleSave} className="bg-green-600 px-6 py-2 rounded-lg hover:bg-green-500 transition">Save Event</button>
                </div>
            </div>
        </div>
    );
};

const SongsTab = ({ user }) => {
    const [songs, setSongs] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSong, setEditingSong] = useState(null);

    // Fetch all songs from the database
    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/songs`), (snapshot) => {
            setSongs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [user]);

    const handleOpenModal = (song = null) => {
        setEditingSong(song);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSong(null);
    };
    
    const handleDeleteSong = async (songId) => {
        if (window.confirm('Are you sure you want to delete this song from the database?')) {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/songs`, songId));
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Song Database</h2>
                <button onClick={() => handleOpenModal()} className="flex items-center space-x-2 bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-500 transition">
                    <PlusCircle size={20} />
                    <span>Add Song</span>
                </button>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg">
                <div className="space-y-2">
                    {songs.map(song => (
                        <div key={song.id} className="flex justify-between items-center p-3 bg-gray-700 rounded">
                            <div>
                                <p className="font-bold">{song.name}</p>
                                <p className="text-sm text-gray-400">Key: {song.key || 'N/A'} | Tempo: {song.tempo || 'N/A'} | Soloist(s): {song.soloists || 'N/A'}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => handleOpenModal(song)} className="p-2 hover:bg-gray-600 rounded-full"><Edit size={18}/></button>
                                <button onClick={() => handleDeleteSong(song.id)} className="p-2 text-red-500 hover:bg-gray-600 rounded-full"><Trash2 size={18}/></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {isModalOpen && <SongModal song={editingSong} onClose={handleCloseModal} />}
        </div>
    );
};

const SongModal = ({ song, onClose }) => {
    const [name, setName] = useState(song?.name || '');
    const [soloists, setSoloists] = useState(song?.soloists || '');
    const [key, setKey] = useState(song?.key || '');
    const [tempo, setTempo] = useState(song?.tempo || '');

    const handleSave = async () => {
        if (!name.trim()) return;
        const songData = { name, soloists, key, tempo };

        if (song) {
            await updateDoc(doc(db, `artifacts/${appId}/public/data/songs`, song.id), songData);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/public/data/songs`), songData);
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg w-full max-w-lg relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X /></button>
                <h2 className="text-2xl font-bold mb-6">{song ? 'Edit Song' : 'Add New Song'}</h2>
                <div className="space-y-4">
                    <div>
                        <label className="text-sm text-gray-400">Song Name</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                    </div>
                    <div>
                        <label className="text-sm text-gray-400">Soloist(s)</label>
                        <input type="text" value={soloists} onChange={e => setSoloists(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm text-gray-400">Key</label>
                            <input type="text" value={key} onChange={e => setKey(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                        </div>
                        <div>
                            <label className="text-sm text-gray-400">Tempo (BPM)</label>
                            <input type="text" value={tempo} onChange={e => setTempo(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                        </div>
                    </div>
                </div>
                <div className="mt-8 flex justify-end">
                    <button onClick={handleSave} className="bg-green-600 px-6 py-2 rounded-lg hover:bg-green-500 transition">Save Song</button>
                </div>
            </div>
        </div>
    );
};

const RolesTab = ({ user }) => {
    const [roles, setRoles] = useState([]);
    const [newRoleName, setNewRoleName] = useState('');
    const [editingRole, setEditingRole] = useState(null);
    const [newChecklistItem, setNewChecklistItem] = useState('');

    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/roles`), (snapshot) => {
            setRoles(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [user]);

    const handleAddRole = async () => {
        if (!newRoleName.trim()) return;
        await addDoc(collection(db, `artifacts/${appId}/public/data/roles`), { name: newRoleName, checklist: [] });
        setNewRoleName('');
    };
    
    const handleDeleteRole = async (roleId) => {
        if (window.confirm('Are you sure you want to delete this role?')) {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/roles`, roleId));
            if (editingRole?.id === roleId) {
                setEditingRole(null);
            }
        }
    };

    const handleAddChecklistItem = async () => {
        if (!newChecklistItem.trim() || !editingRole) return;
        const roleRef = doc(db, `artifacts/${appId}/public/data/roles`, editingRole.id);
        await updateDoc(roleRef, {
            checklist: arrayUnion({ task: newChecklistItem })
        });
        setNewChecklistItem('');
        const updatedRoleDoc = await getDoc(roleRef);
        setEditingRole({id: updatedRoleDoc.id, ...updatedRoleDoc.data()});
    };

    const handleDeleteChecklistItem = async (item) => {
        if (!editingRole) return;
        const roleRef = doc(db, `artifacts/${appId}/public/data/roles`, editingRole.id);
        await updateDoc(roleRef, {
            checklist: arrayRemove(item)
        });
        const updatedRoleDoc = await getDoc(roleRef);
        setEditingRole({id: updatedRoleDoc.id, ...updatedRoleDoc.data()});
    };

    const handleMoveChecklistItem = async (index, direction) => {
        if (!editingRole) return;
        const list = [...editingRole.checklist];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= list.length) return;
        
        [list[index], list[newIndex]] = [list[newIndex], list[index]]; // Swap elements

        const roleRef = doc(db, `artifacts/${appId}/public/data/roles`, editingRole.id);
        await updateDoc(roleRef, { checklist: list });
        setEditingRole({...editingRole, checklist: list });
    };

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">Roles & Checklists</h2>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold mb-4">All Roles</h3>
                    <div className="flex space-x-2 mb-4">
                        <input type="text" value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="New role name" className="flex-grow p-2 bg-gray-700 rounded" />
                        <button onClick={handleAddRole} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-500">Add</button>
                    </div>
                    <div className="space-y-2">
                        {roles.map(role => (
                            <div key={role.id} className={`flex items-center justify-between p-3 rounded cursor-pointer ${editingRole?.id === role.id ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}>
                                <span className="flex-grow" onClick={() => setEditingRole(role)}>{role.name}</span>
                                <button onClick={() => handleDeleteRole(role.id)} className="ml-2 text-red-500 hover:text-red-400 p-1 rounded-full hover:bg-gray-500"><Trash2 size={16}/></button>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg">
                    {editingRole ? (
                        <>
                            <h3 className="text-xl font-semibold mb-4">Checklist for {editingRole.name}</h3>
                             <div className="flex space-x-2 mb-4">
                                <input type="text" value={newChecklistItem} onChange={e => setNewChecklistItem(e.target.value)} placeholder="New checklist item" className="flex-grow p-2 bg-gray-700 rounded" />
                                <button onClick={handleAddChecklistItem} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-500">Add</button>
                            </div>
                            <div className="space-y-2">
                                {editingRole.checklist?.map((item, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                                        <span>{item.task}</span>
                                        <div className="flex items-center">
                                            <button onClick={() => handleMoveChecklistItem(index, 'up')} className="p-1 hover:bg-gray-600 rounded-full disabled:opacity-50" disabled={index === 0}><ArrowUp size={16}/></button>
                                            <button onClick={() => handleMoveChecklistItem(index, 'down')} className="p-1 hover:bg-gray-600 rounded-full disabled:opacity-50" disabled={index === editingRole.checklist.length - 1}><ArrowDown size={16}/></button>
                                            <button onClick={() => handleDeleteChecklistItem(item)} className="p-1 text-red-500 hover:bg-gray-600 rounded-full"><Trash2 size={16}/></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-gray-400">Select a role to edit its checklist.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const UsersTab = ({ user }) => {
    const [users, setUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/users`), (snapshot) => {
            setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [user]);

    const handleOpenModal = (user = null) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingUser(null);
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to remove this user?')) {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/users`, userId));
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Manage Users</h2>
                 <button onClick={() => handleOpenModal()} className="flex items-center space-x-2 bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-500 transition">
                    <PlusCircle size={20} />
                    <span>Add User</span>
                </button>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                {users.map(u => (
                    <div key={u.id} className="flex justify-between items-center p-2 bg-gray-700 rounded">
                        <div>
                            <p>{u.name}</p>
                            <p className="text-sm text-gray-400">{u.phoneNumber || 'No phone number'}</p>
                        </div>
                        <div>
                            <button onClick={() => handleOpenModal(u)} className="p-2 hover:bg-gray-600 rounded-full"><Edit size={16} /></button>
                            <button onClick={() => handleDeleteUser(u.id)} className="text-red-500 hover:text-red-400 p-1 rounded-full hover:bg-gray-600">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {isModalOpen && <UserModal user={editingUser} onClose={handleCloseModal} />}
        </div>
    );
};

const UserModal = ({ user, onClose }) => {
    const [name, setName] = useState(user?.name || '');
    const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');

    const handleSave = async () => {
        if (!name.trim()) return;
        const userData = { name, phoneNumber };

        if (user) {
            await updateDoc(doc(db, `artifacts/${appId}/public/data/users`, user.id), userData);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/public/data/users`), userData);
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg w-full max-w-lg relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X /></button>
                <h2 className="text-2xl font-bold mb-6">{user ? 'Edit User' : 'Add New User'}</h2>
                <div className="space-y-4">
                    <div>
                        <label className="text-sm text-gray-400">User Name</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                    </div>
                    <div>
                        <label className="text-sm text-gray-400">Phone Number (for SMS alerts)</label>
                        <input type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="e.g., +15551234567" className="w-full mt-1 p-2 bg-gray-700 rounded" />
                    </div>
                </div>
                <div className="mt-8 flex justify-end">
                    <button onClick={handleSave} className="bg-green-600 px-6 py-2 rounded-lg hover:bg-green-500 transition">Save User</button>
                </div>
            </div>
        </div>
    );
};


const MilestonesTab = ({ user }) => {
    const [milestones, setMilestones] = useState([]);
    const [eventOptions, setEventOptions] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [currentMilestone, setCurrentMilestone] = useState({ id: null, name: '', time: '', redThreshold: 0, yellowThreshold: 30, howtoLink: '', assignedEventTypes: [] });
    const [isLinkValid, setIsLinkValid] = useState(true);

    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(collection(db, `artifacts/${appId}/public/data/milestones`), (snapshot) => {
            const sortedMilestones = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (a.time || "23:59").localeCompare(b.time || "23:59"));
            setMilestones(sortedMilestones);
        });
        
        const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'leaderSettings');
        const unsubOptions = onSnapshot(settingsRef, (doc) => {
            if (doc.exists()) {
                setEventOptions(doc.data()?.eventOptions || []);
            }
        });
        
        return () => {
            unsub();
            unsubOptions();
        };
    }, [user]);

    const resetForm = () => {
        setIsEditing(false);
        setCurrentMilestone({ id: null, name: '', time: '', redThreshold: 0, yellowThreshold: 30, howtoLink: '', assignedEventTypes: [] });
        setIsLinkValid(true);
    };

    const handleEditClick = (milestone) => {
        setIsEditing(true);
        setCurrentMilestone({ ...{ id: null, name: '', time: '', redThreshold: 0, yellowThreshold: 30, howtoLink: '', assignedEventTypes: [] }, ...milestone});
        setIsLinkValid(true);
    };

    const handleLinkChange = (e) => {
        const link = e.target.value;
        setCurrentMilestone({ ...currentMilestone, howtoLink: link });
        if (link === '' || getYoutubeEmbedUrl(link)) {
            setIsLinkValid(true);
        } else {
            setIsLinkValid(false);
        }
    };
    
    const handleEventTypeToggle = (eventType) => {
        const currentAssigned = currentMilestone.assignedEventTypes || [];
        const isAssigned = currentAssigned.includes(eventType);
        let newAssigned = [];
        if (isAssigned) {
            newAssigned = currentAssigned.filter(type => type !== eventType);
        } else {
            newAssigned = [...currentAssigned, eventType];
        }
        setCurrentMilestone({ ...currentMilestone, assignedEventTypes: newAssigned });
    };

    const handleSubmit = async () => {
        if (!currentMilestone.name.trim() || !isLinkValid) return;
        const dataToSave = {
            name: currentMilestone.name,
            time: currentMilestone.time || null,
            redThreshold: Number(currentMilestone.redThreshold),
            yellowThreshold: Number(currentMilestone.yellowThreshold),
            howtoLink: currentMilestone.howtoLink || null,
            assignedEventTypes: currentMilestone.assignedEventTypes || []
        };

        if (isEditing && currentMilestone.id) {
            await updateDoc(doc(db, `artifacts/${appId}/public/data/milestones`, currentMilestone.id), dataToSave);
        } else {
            await addDoc(collection(db, `artifacts/${appId}/public/data/milestones`), dataToSave);
        }
        resetForm();
    };

    const handleDeleteMilestone = async (id) => {
        if (window.confirm('Are you sure you want to delete this milestone?')) {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/milestones`, id));
        }
    };

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">Configure Milestones</h2>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold mb-4">{isEditing ? 'Edit Milestone' : 'Add New Milestone'}</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm text-gray-400">Name</label>
                            <input type="text" value={currentMilestone.name} onChange={e => setCurrentMilestone({...currentMilestone, name: e.target.value})} placeholder="e.g., Doors Open" className="w-full mt-1 p-2 bg-gray-700 rounded" />
                        </div>
                        <div>
                            <label className="text-sm text-gray-400">Default Time (optional)</label>
                            <input type="time" value={currentMilestone.time} onChange={e => setCurrentMilestone({...currentMilestone, time: e.target.value})} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                        </div>
                        <div>
                            <label className="text-sm text-gray-400">How-to Link (Optional)</label>
                            <input type="text" value={currentMilestone.howtoLink} onChange={handleLinkChange} placeholder="e.g., https://www.youtube.com/watch?v=..." className={`w-full mt-1 p-2 bg-gray-700 rounded ${!isLinkValid ? 'border-2 border-red-500' : ''}`} />
                            {!isLinkValid && <p className="text-red-500 text-xs mt-1">Please enter a valid YouTube link.</p>}
                        </div>
                         <div>
                            <label className="text-sm text-gray-400 mb-2 block">Assign to Event Types</label>
                            <div className="grid grid-cols-2 gap-2">
                                {eventOptions.map(option => (
                                    <label key={option} className="flex items-center space-x-2 p-2 bg-gray-700 rounded">
                                        <input 
                                            type="checkbox" 
                                            checked={(currentMilestone.assignedEventTypes || []).includes(option)}
                                            onChange={() => handleEventTypeToggle(option)}
                                            className="form-checkbox h-5 w-5 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                                        />
                                        <span>{option}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-gray-400">Yellow Threshold (mins)</label>
                                <input type="number" value={currentMilestone.yellowThreshold} onChange={e => setCurrentMilestone({...currentMilestone, yellowThreshold: e.target.value})} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                            </div>
                             <div>
                                <label className="text-sm text-gray-400">Red Threshold (mins)</label>
                                <input type="number" value={currentMilestone.redThreshold} onChange={e => setCurrentMilestone({...currentMilestone, redThreshold: e.target.value})} className="w-full mt-1 p-2 bg-gray-700 rounded" />
                            </div>
                        </div>
                        <div className="flex space-x-2">
                            <button onClick={handleSubmit} disabled={!isLinkValid} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-500 disabled:bg-gray-500">{isEditing ? 'Update' : 'Add'}</button>
                            {isEditing && <button onClick={resetForm} className="bg-gray-600 px-4 py-2 rounded hover:bg-gray-500">Cancel</button>}
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                     <h3 className="text-xl font-semibold mb-2 p-2">Existing Milestones</h3>
                    {milestones.map(m => (
                        <div key={m.id} className="flex justify-between items-center p-2 bg-gray-700 rounded">
                            <div>
                                <p className="flex items-center space-x-2">{m.name} {m.howtoLink && <PlayCircle size={16} className="text-blue-400"/>} <span className="text-gray-400 text-sm">{m.time ? `(${m.time})` : ''}</span></p>
                                <p className="text-xs text-gray-500">
                                    <span className="text-yellow-400">Yellow</span>: {m.yellowThreshold}m, <span className="text-red-400">Red</span>: {m.redThreshold}m
                                </p>
                                {m.assignedEventTypes && m.assignedEventTypes.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-1">
                                       Events: {m.assignedEventTypes.join(', ')}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center space-x-1">
                                <button onClick={() => handleEditClick(m)} className="p-2 hover:bg-gray-600 rounded-full"><Edit size={16} /></button>
                                <button onClick={() => handleDeleteMilestone(m.id)} className="text-red-500 hover:text-red-400 p-2 hover:bg-gray-600 rounded-full">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AuditLogTab = ({ user }) => {
    const [allEvents, setAllEvents] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [filters, setFilters] = useState([{ field: 'eventName', qualifier: 'contains', value: '' }]);
    const [filterLogic, setFilterLogic] = useState('AND');
    const [expandedEventId, setExpandedEventId] = useState(null);
    const [expandedChecklist, setExpandedChecklist] = useState(null); // Tracks expanded user checklist: { eventId, userId }

    // Fetch all necessary data for the audit log
    useEffect(() => {
        if (!user) return;
        const unsubEvents = onSnapshot(collection(db, `artifacts/${appId}/public/data/events`), (snapshot) => {
            setAllEvents(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubUsers = onSnapshot(collection(db, `artifacts/${appId}/public/data/users`), (snapshot) => {
            setAllUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubRoles = onSnapshot(collection(db, `artifacts/${appId}/public/data/roles`), (snapshot) => {
            setAllRoles(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubEvents(); unsubUsers(); unsubRoles(); };
    }, [user]);

    // Apply filters whenever the source data or filter criteria change
    useEffect(() => {
        if (allEvents.length === 0) {
            setFilteredEvents([]);
            return;
        }

        const activeFilters = filters.filter(f => f.value.trim() !== '');
        if (activeFilters.length === 0) {
            setFilteredEvents(allEvents);
            return;
        }

        const results = allEvents.filter(event => {
            const checkCondition = (filter) => {
                const { field, qualifier, value } = filter;
                const lowerCaseValue = value.toLowerCase();

                switch (field) {
                    case 'eventName':
                        return event.name.toLowerCase().includes(lowerCaseValue);
                    case 'date':
                        const eventDate = event.date.toDate();
                        const filterDate = new Date(value);
                        if (qualifier === 'is') return eventDate.toDateString() === filterDate.toDateString();
                        if (qualifier === 'isBefore') return eventDate < filterDate;
                        if (qualifier === 'isAfter') return eventDate > filterDate;
                        return false;
                    case 'checkedInUser':
                        return event.checkIns?.some(ci => ci.name.toLowerCase().includes(lowerCaseValue));
                    case 'song':
                        return event.songs?.some(s => s.name.toLowerCase().includes(lowerCaseValue));
                    case 'milestoneStatus':
                        const [name, status] = value.split(':').map(s => s.trim().toLowerCase());
                        const isCompleted = event.milestoneCompletions?.some(mc => mc.milestoneName.toLowerCase() === name);
                        if (status === 'completed') return isCompleted;
                        if (status === 'pending') return !isCompleted;
                        return false;
                    default:
                        return true;
                }
            };

            if (filterLogic === 'AND') {
                return activeFilters.every(checkCondition);
            } else { // OR logic
                return activeFilters.some(checkCondition);
            }
        });
        setFilteredEvents(results);

    }, [filters, filterLogic, allEvents]);

    const handleFilterChange = (index, key, value) => {
        const newFilters = [...filters];
        newFilters[index][key] = value;
        setFilters(newFilters);
    };

    const addFilter = () => {
        setFilters([...filters, { field: 'eventName', qualifier: 'contains', value: '' }]);
    };
    
    const removeFilter = (index) => {
        setFilters(filters.filter((_, i) => i !== index));
    };
    
    const toggleChecklist = (eventId, userId) => {
        if (expandedChecklist?.eventId === eventId && expandedChecklist?.userId === userId) {
            setExpandedChecklist(null);
        } else {
            setExpandedChecklist({ eventId, userId });
        }
    };

    const filterFields = {
        eventName: { label: 'Event Name', qualifiers: ['contains'] },
        date: { label: 'Date', qualifiers: ['is', 'isBefore', 'isAfter'], type: 'date' },
        checkedInUser: { label: 'Checked-in User', qualifiers: ['contains'] },
        song: { label: 'Song', qualifiers: ['contains'] },
        milestoneStatus: { label: 'Milestone Status', qualifiers: ['is'], placeholder: 'Milestone Name: completed/pending' },
    };

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">Audit Log</h2>
            
            <div className="bg-gray-800 p-6 rounded-lg mb-8">
                <div className="flex items-center space-x-2 mb-4">
                    <Filter size={20} />
                    <h3 className="text-xl font-semibold">Filter Events</h3>
                </div>
                <div className="space-y-4">
                    {filters.map((filter, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            <select value={filter.field} onChange={e => handleFilterChange(index, 'field', e.target.value)} className="p-2 bg-gray-700 rounded">
                                {Object.entries(filterFields).map(([key, { label }]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                            <select value={filter.qualifier} onChange={e => handleFilterChange(index, 'qualifier', e.target.value)} className="p-2 bg-gray-700 rounded">
                                {filterFields[filter.field].qualifiers.map(q => <option key={q} value={q}>{q}</option>)}
                            </select>
                            <input 
                                type={filterFields[filter.field].type || 'text'}
                                placeholder={filterFields[filter.field].placeholder || 'Enter value...'}
                                value={filter.value} 
                                onChange={e => handleFilterChange(index, 'value', e.target.value)} 
                                className="flex-grow p-2 bg-gray-700 rounded" 
                            />
                            <button onClick={() => removeFilter(index)} className="p-2 bg-red-600 rounded hover:bg-red-500"><Minus size={16} /></button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center mt-4 space-x-4">
                    <button onClick={addFilter} className="flex items-center space-x-1 p-2 bg-blue-600 rounded hover:bg-blue-500"><Plus size={16} /><span>Add Rule</span></button>
                    <div className="flex items-center space-x-2">
                        <label>Logic:</label>
                        <select value={filterLogic} onChange={e => setFilterLogic(e.target.value)} className="p-2 bg-gray-700 rounded">
                            <option value="AND">AND</option>
                            <option value="OR">OR</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {filteredEvents.map(event => (
                    <div key={event.id} className="bg-gray-800 rounded-lg">
                        <button onClick={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)} className="w-full text-left p-4 hover:bg-gray-700 rounded-t-lg flex justify-between items-center">
                           <div>
                                <h3 className="text-xl font-bold">{event.name}</h3>
                                <span className="text-gray-400">{new Date(event.date.seconds * 1000).toLocaleDateString()}</span>
                           </div>
                           {expandedEventId === event.id ? <ChevronDown /> : <ChevronRight />}
                        </button>
                        {expandedEventId === event.id && (
                            <div className="p-4 border-t border-gray-700 grid md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold mb-2 text-lg">Check-ins & Checklist Status</h4>
                                    <div className="space-y-2">
                                        {event.checkIns?.map(ci => {
                                            const role = allRoles.find(r => r.id === ci.roleId);
                                            const totalTasks = role?.checklist?.length || 0;
                                            const completedTasks = event.auditLog?.filter(log => log.roleId === ci.roleId && log.completions.some(c => c.userId === ci.userId)).length || 0;
                                            const isChecklistExpanded = expandedChecklist?.eventId === event.id && expandedChecklist?.userId === ci.userId;
                                            return (
                                                <div key={ci.userId} className="bg-gray-700 rounded p-2">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <p className="font-bold">{ci.name} <span className="font-normal text-gray-400">- {role?.name || 'N/A'}</span></p>
                                                            <p className="text-sm">{totalTasks > 0 ? `${completedTasks}/${totalTasks} completed` : 'No checklist'}</p>
                                                        </div>
                                                        {totalTasks > 0 && (
                                                            <button onClick={() => toggleChecklist(event.id, ci.userId)} className="p-1 hover:bg-gray-600 rounded-full">
                                                                <ClipboardList size={18} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {isChecklistExpanded && (
                                                        <div className="mt-2 pt-2 border-t border-gray-600 space-y-1 text-sm">
                                                            {role.checklist.map((item, index) => {
                                                                const isCompleted = event.auditLog?.some(log => log.task === item.task && log.roleId === ci.roleId && log.completions.some(c => c.userId === ci.userId));
                                                                return (
                                                                    <div key={index} className={`flex items-center space-x-2 ${isCompleted ? 'text-green-400' : 'text-red-400'}`}>
                                                                        {isCompleted ? <CheckCircle size={14} /> : <X size={14} />}
                                                                        <span>{item.task}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {(!event.checkIns || event.checkIns.length === 0) && <p className="text-gray-500">No users checked in.</p>}
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-semibold mb-2 text-lg">Milestone Status</h4>
                                     <ul className="space-y-1">
                                        {event.milestones?.map((m, i) => {
                                            const isCompleted = event.milestoneCompletions?.some(mc => mc.milestoneName === m.name);
                                            return (
                                                <li key={i} className={`flex items-center space-x-2 ${isCompleted ? 'text-green-400' : 'text-gray-300'}`}>
                                                    {isCompleted ? <CheckCircle size={14} /> : <Minus size={14} />}
                                                    <span>{m.name}: {isCompleted ? 'Completed' : 'Pending'}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                                <div className="md:col-span-2">
                                    <h4 className="font-semibold mb-2 text-lg">Set List</h4>
                                    <p className="text-sm text-gray-400">{event.songs?.map(s => s.name).join(', ') || 'No songs in set list.'}</p>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};


const SettingsTab = ({ user }) => {
    const [pin, setPin] = useState('');
    const [eventOptions, setEventOptions] = useState([]);
    const [newEventOption, setNewEventOption] = useState('');
    const [message, setMessage] = useState('');

    const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'leaderSettings');

    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setPin(data.pin || '');
                setEventOptions(data.eventOptions || []);
            }
        });
        return () => unsub();
    }, [user]);

    const handleSave = async () => {
        if (!user) {
            setMessage('You must be logged in to save settings.');
            return;
        }
        try {
            await setDoc(settingsRef, { pin, eventOptions }, { merge: true });
            setMessage('Settings saved successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error saving settings: ", error);
            setMessage('Error saving settings.');
        }
    };
    
    const addEventOption = () => {
        if(newEventOption && !eventOptions.includes(newEventOption)) {
            const updatedOptions = [...eventOptions, newEventOption];
            setEventOptions(updatedOptions);
            setNewEventOption('');
        }
    };

    const deleteEventOption = (optionToDelete) => {
        const updatedOptions = eventOptions.filter(opt => opt !== optionToDelete);
        setEventOptions(updatedOptions);
    };

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">Console Settings</h2>
            <div className="space-y-8 max-w-lg">
                <div className="bg-gray-800 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold mb-4">Leader PIN</h3>
                    <input type="text" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="4-digit PIN" maxLength="4" className="w-full p-2 bg-gray-700 rounded" />
                </div>
                <div className="bg-gray-800 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold mb-4">Event Name Options</h3>
                    <div className="flex space-x-2 mb-4">
                        <input type="text" value={newEventOption} onChange={e => setNewEventOption(e.target.value)} placeholder="Add new option" className="flex-grow p-2 bg-gray-700 rounded" />
                        <button onClick={addEventOption} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-500">Add</button>
                    </div>
                    <div className="space-y-2">
                        {eventOptions.map((opt, i) => (
                            <div key={i} className="flex justify-between items-center p-2 bg-gray-700 rounded">
                                <span>{opt}</span>
                                <button onClick={() => deleteEventOption(opt)} className="text-red-500 hover:text-red-400 p-1 rounded-full hover:bg-gray-600">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
                <button onClick={handleSave} className="bg-green-600 px-6 py-3 rounded-lg text-lg hover:bg-green-500">Save All Settings</button>
                {message && <p className="mt-4 text-green-400">{message}</p>}
            </div>
        </div>
    );
};

const UserSwitcherModal = ({ event, onSelect, onClose }) => {
    const handleSelect = (user) => {
        onSelect(user);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg w-full max-w-md relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X /></button>
                <h2 className="text-2xl font-bold mb-6">Switch Active User</h2>
                <div className="space-y-2">
                    {event?.checkIns?.map(user => (
                        <button key={user.userId} onClick={() => handleSelect(user)} className="w-full text-left p-3 bg-gray-700 rounded hover:bg-gray-600 transition">
                            {user.name}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const EventEditorModal = ({ event, onClose }) => {
    // This modal is now a duplicate of the song management part of the leader's EventModal
    const [songs, setSongs] = useState([]);
    const [eventSongs, setEventSongs] = useState(event?.songs || []);
    const [selectedSongId, setSelectedSongId] = useState('');

    useEffect(() => {
        const unsubSongs = onSnapshot(collection(db, `artifacts/${appId}/public/data/songs`), (snapshot) => {
            setSongs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsubSongs();
    }, []);

    const handleAddSong = () => {
        if (!selectedSongId) return;
        const songToAdd = songs.find(s => s.id === selectedSongId);
        if (songToAdd) {
            setEventSongs(prev => [...prev, songToAdd]);
        }
        setSelectedSongId('');
    };

    const handleRemoveSong = (indexToRemove) => {
        setEventSongs(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleMoveSong = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= eventSongs.length) return;
        const newList = [...eventSongs];
        [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
        setEventSongs(newList);
    };

    const handleSave = async () => {
        const eventRef = doc(db, `artifacts/${appId}/public/data/events`, event.id);
        await updateDoc(eventRef, { songs: eventSongs });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-8 rounded-lg w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X /></button>
                <h2 className="text-2xl font-bold mb-6">Edit Set List</h2>
                <div>
                    <div className="flex space-x-2 mb-4">
                        <select value={selectedSongId} onChange={e => setSelectedSongId(e.target.value)} className="flex-grow p-2 bg-gray-700 rounded">
                            <option value="">Select a song to add...</option>
                            {songs.map(song => <option key={song.id} value={song.id}>{song.name}</option>)}
                        </select>
                        <button onClick={handleAddSong} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-500">Add</button>
                    </div>
                    <div className="space-y-2">
                        {eventSongs.map((song, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                                <span>{index + 1}. {song.name}</span>
                                <div className="flex items-center">
                                    <button onClick={() => handleMoveSong(index, 'up')} className="p-1 hover:bg-gray-600 rounded-full disabled:opacity-50" disabled={index === 0}><ArrowUp size={16}/></button>
                                    <button onClick={() => handleMoveSong(index, 'down')} className="p-1 hover:bg-gray-600 rounded-full disabled:opacity-50" disabled={index === eventSongs.length - 1}><ArrowDown size={16}/></button>
                                    <button onClick={() => handleRemoveSong(index)} className="p-1 text-red-500 hover:bg-gray-600 rounded-full"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mt-8 flex justify-end">
                    <button onClick={handleSave} className="bg-green-600 px-6 py-2 rounded-lg hover:bg-green-500 transition">Save Changes</button>
                </div>
            </div>
        </div>
    );
};

